import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Breadcrumbs,
  Card,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import JumboSearch from "@jumbo/components/JumboSearch";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  getShoppingCartRules,
  createShoppingCartRule,
  updateShoppingCartRules,
  deleteShoppingCartRule,
} from "app/services/api/shoppingCart";
import RuleItem from "./RuleItem";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRule = ({ rule, disabled, onEdit, onDelete, onDuplicate }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rule._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
  };
  return (
    <div ref={setNodeRef} style={style}>
      <RuleItem
        item={rule}
        disabled={disabled}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        dragHandleAttributes={attributes}
        dragHandleListeners={listeners}
      />
    </div>
  );
};

const DSOKCartRules = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [rulesState, setRulesState] = useState([]);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        const rules = await getShoppingCartRules();
        setRulesState(Array.isArray(rules) ? rules : []);
      } catch (e) {
        setRulesState([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRules();
  }, []);

  // Determine if we're in auto-discounts mode based on the current route
  const isAutoDiscountsMode = location.pathname.includes(
    "shopping-cart-auto-discounts"
  );

  const rules = useMemo(() => {
    let list = Array.isArray(rulesState) ? rulesState : [];

    // Filter by autoApply based on route
    list = list.filter((r) => Boolean(r.autoApply) === isAutoDiscountsMode);

    // Apply search filter if needed
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(s) ||
        (r.discountType || "").toLowerCase().includes(s) ||
        String(r.value ?? "")
          .toLowerCase()
          .includes(s)
    );
  }, [search, rulesState, isAutoDiscountsMode]);

  const handleDelete = (rule) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSureDeleteRule", {
        defaultValue: "Are you sure?",
      }),
      onYes: async () => {
        try {
          await deleteShoppingCartRule(rule._id);
          setRulesState((prev) =>
            (prev || []).filter((r) => r._id !== rule._id)
          );
          enqueueSnackbar(
            `${t("buttons.delete", { defaultValue: "Delete" })} ${t(
              "notifications.wasSuccessful",
              { defaultValue: "was successful" }
            )}`,
            { variant: "success" }
          );
        } catch (e) {
          enqueueSnackbar(
            t("notifications.deleteFailed", { defaultValue: "Delete failed" }),
            { variant: "error" }
          );
        } finally {
          hideDialog();
        }
      },
      onNo: () => hideDialog(),
    });
  };

  const handleDuplicate = (rule) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure", {
        defaultValue: "Are you sure?",
      }),
      content: t("pages.dsokCartRules.confirmDuplicate", {
        defaultValue: "Do you really want to duplicate this rule?",
      }),
      onYes: async () => {
        hideDialog();
        try {
          const { _id, order, ...rest } = rule || {};
          const copy = {
            ...rest,
            name: `${rule?.name || ""} (copy)`,
            order: (rulesState?.length || 0) + 1,
          };
          const created = await createShoppingCartRule(copy);
          if (created && created._id) {
            setRulesState((prev) => [...(prev || []), created]);
            enqueueSnackbar(
              `${t("buttons.copy", { defaultValue: "Copy" })} ${t(
                "notifications.wasSuccessful",
                { defaultValue: "was successful" }
              )}`,
              { variant: "success" }
            );
            navigate(`/customer/dsok/cart-rules/${created._id}`);
          } else {
            enqueueSnackbar(
              t("notifications.wasFailed", { defaultValue: "was failed" }),
              { variant: "error" }
            );
          }
        } catch (e) {
          enqueueSnackbar(
            t("notifications.wasFailed", { defaultValue: "was failed" }),
            { variant: "error" }
          );
        }
      },
      onNo: () => hideDialog(),
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event || {};
    if (!over || active?.id === over?.id) return;
    setRulesState((prev) => {
      const oldIndex = (prev || []).findIndex((r) => r._id === active.id);
      const newIndex = (prev || []).findIndex((r) => r._id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const moved = arrayMove(prev, oldIndex, newIndex);
      const updated = moved.map((r, idx) => ({ ...r, order: idx + 1 }));
      // Persist full ordering (best-effort, async)
      try {
        updateShoppingCartRules(updated);
      } catch (_e) {
        // ignore
      }
      return updated;
    });
  };

  const listTitle = isAutoDiscountsMode
    ? t("sidebar.menuItem.automaticDiscounts")
    : t("pages.dsokCartRules.title");

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link
          to="/customer/dsok"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          {t("pages.pos.breadcrumbDsok")}
        </Link>
        <Typography color="text.primary">{listTitle}</Typography>
      </Breadcrumbs>
      <Typography variant={"h2"} mb={3}>
        {listTitle}
      </Typography>
      <Stack direction="row" alignItems="center" mb={2} spacing={1}>
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="DSOKCartRules"
            onChange={setSearch}
            sx={{ width: "100%" }}
            placeholder={t("pages.presentation.searchPresentation")}
          />
        </Card>
        <Tooltip
          title={t("pages.dsokCartRules.addRule", { defaultValue: "Add Rule" })}
        >
          <IconButton
            onClick={() =>
              navigate(
                location.pathname ===
                  "/customer/dsok/shopping-cart-auto-discounts"
                  ? "/customer/dsok/cart-rules/new/auto"
                  : "/customer/dsok/cart-rules/new"
              )
            }
          >
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : rules?.length ? (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={(rules || []).map((r) => r._id)}
            strategy={verticalListSortingStrategy}
          >
            {(rules || []).map((rule) => (
              <SortableRule
                key={rule._id}
                rule={rule}
                disabled={loading}
                onEdit={() => navigate(`/customer/dsok/cart-rules/${rule._id}`)}
                onDelete={handleDelete}
                onDuplicate={() => handleDuplicate(rule)}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default DSOKCartRules;
